import * as Sentry from "@sentry/vue";
import { PendingSyncError } from "@/utils/errors";
import { pendingChangesPayload } from "@/composables/network/pendingExport";

const FLUSH_DELAY_MS = 5 * 1000;

const queued = new Map();
let flushTimer = null;

const failureLabel = (error) =>
  error?.response?.status ? `HTTP ${error.response.status}` : error?.name;

const sendReport = (group, exported) => {
  const userId = exported.user.uuid;
  const payload = { ...exported, user: { uuid: userId } };
  const label = String(failureLabel(group.error));
  Sentry.withScope((scope) => {
    scope.setTags({
      pending_sync: group.kind,
      pending_table: group.table,
      http_status: String(group.error?.response?.status ?? "none"),
    });
    scope.setContext("pending_sync", {
      kind: group.kind,
      projectUuid: group.projectUuid,
      keys: group.keys,
      page: group.page,
      keptSince: group.keptSince
        ? new Date(group.keptSince).toISOString()
        : null,
    });
    scope.setFingerprint(["pending-sync", group.kind, group.table, label]);
    if (userId) scope.setUser({ id: userId });
    scope.addAttachment({
      filename: "modifications-en-attente.json",
      data: JSON.stringify(payload, null, 2),
      contentType: "application/json",
    });
    Sentry.captureException(
      new PendingSyncError(
        `Pending change ${group.kind}: ${group.table} (${label})`,
        { cause: group.error }
      )
    );
  });
};

export const flushSyncProblems = async () => {
  clearTimeout(flushTimer);
  const groups = [...queued.values()];
  queued.clear();
  const exports = new Map();
  for (const group of groups) {
    try {
      if (!exports.has(group.projectUuid))
        exports.set(
          group.projectUuid,
          await pendingChangesPayload({ projectUuid: group.projectUuid })
        );
      sendReport(group, exports.get(group.projectUuid));
    } catch (reportError) {
      console.error("Pending sync problem not reported", reportError);
      Sentry.captureException(group.error, {
        tags: { pending_sync: group.kind, pending_table: group.table },
        contexts: {
          pending_sync: { keys: group.keys, reportError: String(reportError) },
        },
      });
    }
  }
};

export const queueSyncProblem = ({ kind, projectUuid, rows, error }) => {
  const table = rows[0]?.table ?? "mission";
  const signature = [projectUuid, kind, table, failureLabel(error)].join("|");
  const group = queued.get(signature) ?? {
    kind,
    projectUuid,
    table,
    error,
    keys: [],
    page: rows[0]?.page ?? null,
    keptSince: null,
  };
  group.keys.push(...rows.map(({ key }) => key));
  const keptAt = [group.keptSince, ...rows.map(({ at }) => at)].filter(Boolean);
  group.keptSince = keptAt.length ? Math.min(...keptAt) : null;
  queued.set(signature, group);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flushSyncProblems, FLUSH_DELAY_MS);
};
